/**
 * useDealsAccess.js
 *
 * Zugriffsregel für den Deal Wizard (/deals) und Value-Based Offers
 * (/value-offer).
 *
 * Der Deal Wizard ist pro World lizenziert, das Flag `hasDealWizard` liegt
 * daher IMMER beim Owner der aktiven World — nie beim eingeloggten User.
 * Daraus folgen zwei Fälle:
 *
 *   Owner / Sub-World:  eigenes Flag entscheidet.
 *   Team-Member:        Flag des World-Owners UND das Feature `offers`
 *                       (bzw. `all`, das der Member-Typ "Full Access"
 *                       vergibt) auf der Mitgliedschaft.
 *
 * Members dürfen `Users/<ownerId>` lesen (siehe firebase-rules.json), das
 * Lizenz-Flag ist für sie also prüfbar.
 */

import { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useTeam } from '../contexts/TeamContext';

export const useDealsAccess = () => {
  const {
    currentUserId,
    activeWorldId,
    features,
    isLoading: isTeamLoading,
    isInitialized,
    isSwitching,
  } = useTeam();

  // null = noch nicht geladen. Wichtig, damit der Route-Guard nicht
  // vorschnell wegredirected, bevor das Flag da ist.
  const [ownerHasDealWizard, setOwnerHasDealWizard] = useState(null);

  useEffect(() => {
    if (!activeWorldId) {
      setOwnerHasDealWizard(null);
      return undefined;
    }

    setOwnerHasDealWizard(null);
    const flagRef = ref(database, `Users/${activeWorldId}/hasDealWizard`);
    const unsubscribe = onValue(
      flagRef,
      (snapshot) => setOwnerHasDealWizard(snapshot.val() === true),
      () => setOwnerHasDealWizard(false)
    );

    return () => unsubscribe();
  }, [activeWorldId]);

  const isOwnWorld = !!activeWorldId && activeWorldId === currentUserId;
  const featureList = Array.isArray(features) ? features : [];
  const grantedByMembership =
    isOwnWorld || featureList.includes('all') || featureList.includes('offers');

  // Ohne aktive World gibt es kein Flag zu laden — sonst hinge der Route-Guard
  // für einen ausgeloggten oder frisch angelegten Account dauerhaft im Spinner.
  const isContextReady = isInitialized && !isTeamLoading && !isSwitching;
  const isFlagPending = !!activeWorldId && ownerHasDealWizard === null;
  const isLoading = !isContextReady || isFlagPending;

  return {
    isLoading,
    hasAccess: !isLoading && ownerHasDealWizard === true && grantedByMembership,
  };
};

export default useDealsAccess;
